/**
 * The store: what is on the two shelves, buying, issuing to the kitchen, and
 * the owner's audits. The store keeper lives on this screen; the owner comes
 * here to count.
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { api } from '../../lib/api.js';
import { useAuth } from '../../stores/auth.js';
import { Tabs } from '../../components/ui/Tabs.jsx';
import { Spinner } from '../../components/ui/Spinner.jsx';
import { StockTab } from './StockTab.jsx';
import { BuyTab } from './BuyTab.jsx';
import { IssuesTab } from './IssuesTab.jsx';
import { AuditTab } from './AuditTab.jsx';
import { SuppliersTab } from './SuppliersTab.jsx';
import { OrdersTab } from './OrdersTab.jsx';

export default function StorePage() {
  const can = useAuth((s) => s.can);
  // What the shop owes suppliers is the owner's business, not the store keeper's.
  const admin = useAuth((s) => s.hasRole('owner', 'super_admin', 'branch_manager'));
  const [tab, setTab] = useState('stock');
  const items = useQuery({ queryKey: ['store', 'items'], queryFn: () => api.get('/store/items'), refetchInterval: 60_000 });

  const tabs = [
    { id: 'stock', label: 'Stock', count: items.data?.totals?.low ? `${items.data.totals.low} low` : undefined },
    can('grn.create.grn') && { id: 'orders', label: 'Orders' },
    can('grn.create.grn') && { id: 'buy', label: 'Buy' },
    can('inventory.create.inventory') && { id: 'issues', label: 'Issue to kitchen' },
    // Audits are the owner's: starting one, and reading what they say about profit.
    can('inventory.approve.inventory') && { id: 'audit', label: 'Audit' },
    admin && { id: 'suppliers', label: 'Suppliers' },
  ].filter(Boolean);

  return (
    <div className="mx-auto max-w-6xl p-4">
      <div className="mb-3 flex flex-wrap items-baseline justify-between gap-3">
        <h1 className="text-xl font-semibold text-ink">Store</h1>
        {items.error && (
          <p role="alert" className="text-sm text-bad">
            {items.error.message}
          </p>
        )}
      </div>
      <Tabs tabs={tabs} active={tab} onChange={setTab} />
      <div className="mt-4">
        {items.isLoading && <Spinner label="Loading the shelves" />}
        {tab === 'stock' && items.data && <StockTab data={items.data} canEdit={can('inventory.update.inventory')} />}
        {tab === 'orders' && items.data && <OrdersTab items={items.data.items} admin={admin} />}
        {tab === 'buy' && items.data && <BuyTab items={items.data.items} admin={admin} />}
        {tab === 'issues' && items.data && <IssuesTab items={items.data.items} />}
        {tab === 'audit' && can('inventory.approve.inventory') && <AuditTab canRun />}
        {tab === 'suppliers' && admin && <SuppliersTab />}
      </div>
    </div>
  );
}
